import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPinned, Link2, Unlink, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { PageHeader, SectionCard, SummaryTile } from "@/components/shared/PageChrome";
import { AsyncPaginatedSelect, directoryFetcher } from "@/components/shared/AsyncPaginatedSelect";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const WINDOW_DAYS = 14;
const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (s, n) => { const d = new Date(`${s}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return iso(d); };
const dayLabel = (s) => new Date(`${s}T00:00:00Z`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", timeZone: "UTC" });

export default function Allocations() {
  const [property, setProperty] = useState(null);
  const [start, setStart] = useState(iso(new Date()));
  const [calendar, setCalendar] = useState(null);
  const [error, setError] = useState(false);
  const [allocateStay, setAllocateStay] = useState(null);
  const [unitChoice, setUnitChoice] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/directory/properties", { params: { page: 1, page_size: 1 } }).then((r) => {
      if (r.data.items[0]) setProperty((cur) => cur || r.data.items[0]); // never overwrite a selection the user made while this loaded
      else setCalendar({ none: true });
    }).catch(() => setError(true));
  }, []);

  const loadCalendar = () => {
    if (!property) return;
    setCalendar(null);
    api.get("/allocations/calendar", { params: { property_id: property.id, start_date: start, end_date: addDays(start, WINDOW_DAYS - 1) } }).then((r) => setCalendar(r.data)).catch(() => setError(true));
  };
  useEffect(() => { loadCalendar(); }, [property?.id, start]); // eslint-disable-line react-hooks/exhaustive-deps

  const days = useMemo(() => Array.from({ length: WINDOW_DAYS }, (_, i) => addDays(start, i)), [start]);
  const today = iso(new Date());

  const allocate = async () => {
    if (!allocateStay || !unitChoice) return;
    try {
      await api.post(`/allocations/stays/${allocateStay.stay_id}/allocate`, { unit_id: unitChoice });
      toast.success(`${allocateStay.booking_reference} allocated to a unit`);
      setAllocateStay(null); setUnitChoice(""); loadCalendar();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not allocate this unit"); }
  };
  const unallocate = async (stayId) => {
    try { await api.post(`/allocations/stays/${stayId}/unallocate`); toast.success("Unit cleared"); loadCalendar(); }
    catch { toast.error("Could not clear this allocation"); }
  };

  if (error) return <ErrorState testId="allocations-error" />;

  const stats = calendar && !calendar.none ? (() => {
    const unitNights = calendar.units.length * WINDOW_DAYS;
    let occupiedNights = 0;
    calendar.units.forEach((u) => u.occupied.forEach((o) => days.forEach((d) => { if (d >= o.check_in_date && d < o.check_out_date) occupiedNights += 1; })));
    return { units: calendar.units.length, stays: calendar.units.reduce((n, u) => n + u.occupied.length, 0), unassigned: calendar.unassigned_stays.length, unitNights, occupiedNights };
  })() : null;

  return (
    <div data-testid="allocations-page" className="min-w-0 space-y-5">
      <PageHeader title="Allocations" subtitle="Unit availability grid per property. A unit cannot be assigned if it would double-book."
        actions={<div className="w-full sm:w-72"><AsyncPaginatedSelect testId="allocations-property-select" value={property?.id || ""} placeholder="Select a property" selectedLabel={property?.name} clearable={false}
          fetchPage={directoryFetcher("/directory/properties")} getSub={(p) => [p.city, p.supplier_name].filter(Boolean).join(" · ")} onChange={(id, item) => item && setProperty(item)} /></div>} />

      {calendar?.none && <EmptyState testId="allocations-no-properties" icon={MapPinned} title="No properties in the directory yet" description="Add a property under Hotels & Properties to start allocating units." />}
      {!calendar && <LoadingState testId="allocations-loading" />}

      {stats && (
        <div data-testid="allocations-summary" className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <SummaryTile testId="alloc-units" label="Units" value={stats.units} sub={calendar.property.name} />
          <SummaryTile testId="alloc-occupancy" label="Unit-nights occupied" value={`${stats.occupiedNights} / ${stats.unitNights}`} sub={`${WINDOW_DAYS}-day window`} />
          <SummaryTile testId="alloc-stays" label="Allocated stays in window" value={stats.stays} />
          <SummaryTile testId="alloc-unassigned" label="Unassigned stays" value={stats.unassigned} sub="Need a unit" tone={stats.unassigned ? "warning" : "default"} />
        </div>
      )}

      {calendar && !calendar.none && (
        <>
          <SectionCard testId="allocations-grid-card" title={`${calendar.property.name} · ${dayLabel(calendar.start_date)} – ${dayLabel(calendar.end_date)}`}
            action={<div className="flex items-center gap-1">
              <Button data-testid="allocations-prev-window" size="sm" variant="outline" onClick={() => setStart(addDays(start, -WINDOW_DAYS))} aria-label="Previous 14 days"><ChevronLeft className="h-4 w-4" /></Button>
              <Button data-testid="allocations-today" size="sm" variant="outline" onClick={() => setStart(today)}>Today</Button>
              <Button data-testid="allocations-next-window" size="sm" variant="outline" onClick={() => setStart(addDays(start, WINDOW_DAYS))} aria-label="Next 14 days"><ChevronRight className="h-4 w-4" /></Button>
            </div>} bodyClassName="px-0 pb-0">
            {calendar.units.length === 0 ? (
              <div className="px-5 pb-5"><EmptyState testId="allocations-no-units" icon={MapPinned} title="No units set up for this property yet" /></div>
            ) : (
              <>
              <ul data-testid="allocations-cards" className="space-y-2 px-4 pb-4 md:hidden" aria-label="Unit availability, one card per unit">
                {calendar.units.map((u) => {
                  const freeNights = days.filter((d) => !u.occupied.some((x) => d >= x.check_in_date && d < x.check_out_date)).length;
                  return (
                    <li key={u.id} data-testid={`allocation-card-${u.id}`} className="rounded-[10px] border border-jigsaw-border bg-white p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div><p className="text-[13px] font-semibold text-jigsaw-navy">{u.unit_type}</p><p className="text-[11px] text-jigsaw-muted">cap. {u.capacity} · {u.occupied.length} stay{u.occupied.length === 1 ? "" : "s"} in window</p></div>
                        <span className="rounded-full bg-jigsaw-bg px-2 py-0.5 text-[11px] font-medium text-jigsaw-navy">{freeNights}/{days.length} nights free</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1" aria-label={`Nights ${dayLabel(days[0])} to ${dayLabel(days[days.length - 1])}`}>
                        {days.map((d) => { const o = u.occupied.some((x) => d >= x.check_in_date && d < x.check_out_date); return <span key={d} title={`${dayLabel(d)} · ${o ? "occupied" : "free"}`} aria-label={`${dayLabel(d)} ${o ? "occupied" : "free"}`} className={cn("h-3 w-4 rounded-sm", o ? "bg-jigsaw-navy" : "bg-jigsaw-bg", d === today && "ring-1 ring-jigsaw-orange")} />; })}
                      </div>
                      {u.occupied.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {u.occupied.map((o) => (
                            <li key={o.stay_id}>
                              <button type="button" data-testid={`allocation-card-stay-${o.stay_id}`} onClick={() => navigate(`/control-centre/bookings/${o.booking_id}`)}
                                className="flex w-full items-center justify-between rounded-md border border-jigsaw-border px-2 py-1.5 text-left text-[12px] hover:bg-jigsaw-bg">
                                <span className="font-mono-ref font-semibold text-jigsaw-navy">{o.booking_reference}</span>
                                <span className="text-jigsaw-muted">{dayLabel(o.check_in_date)} → {dayLabel(o.check_out_date)}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="relative hidden min-w-0 max-w-full overflow-x-auto md:block">
                <table data-testid="allocations-grid" className="w-full min-w-[900px] border-collapse text-[12px]">
                  <thead>
                    <tr className="border-y border-jigsaw-border bg-jigsaw-bg/60">
                      <th className="md:sticky left-0 z-10 bg-jigsaw-bg px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-jigsaw-muted">Unit</th>
                      {days.map((d) => <th key={d} className={cn("px-1 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.04em] text-jigsaw-muted", d === today && "text-jigsaw-orange")}>{dayLabel(d)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {calendar.units.map((u) => (
                      <tr key={u.id} data-testid={`allocation-unit-${u.id}`} className="border-b border-jigsaw-border">
                        <td className="md:sticky left-0 z-10 bg-white px-4 py-2 align-top">
                          <p className="text-[13px] font-medium text-jigsaw-navy">{u.unit_type}</p>
                          <p className="text-[11px] text-jigsaw-muted">cap. {u.capacity} · {u.occupied.length} stay{u.occupied.length === 1 ? "" : "s"}</p>
                        </td>
                        {days.map((d) => {
                          const o = u.occupied.find((x) => d >= x.check_in_date && d < x.check_out_date);
                          const first = o && (d === o.check_in_date || d === days[0]);
                          return (
                            <td key={d} className={cn("h-11 border-l border-jigsaw-border/60 p-0.5 align-middle", d === today && "bg-jigsaw-orange/5")}>
                              {o ? (
                                <button type="button" data-testid={first ? `allocation-occupied-${o.stay_id}` : undefined} title={`${o.booking_reference} · ${o.check_in_date} → ${o.check_out_date}`}
                                  onClick={() => navigate(`/control-centre/bookings/${o.booking_id}`)}
                                  className={cn("flex h-9 w-full items-center bg-jigsaw-navy text-left text-white hover:bg-jigsaw-navy/90", d === o.check_in_date && "rounded-l-md", addDays(d, 1) === o.check_out_date && "rounded-r-md")}>
                                  {first && <span className="font-mono-ref truncate px-1.5 text-[11px] font-semibold">{o.booking_reference}</span>}
                                </button>
                              ) : <span className="sr-only">Free</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </SectionCard>

          {calendar.units.some((u) => u.occupied.length) && (
            <SectionCard testId="allocations-occupied-list" title="Allocated stays in this window" subtitle="Clear a unit to return it to the pool - the stay stays booked.">
              <ul className="divide-y divide-jigsaw-border">
                {calendar.units.flatMap((u) => u.occupied.map((o) => (
                  <li key={o.stay_id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-[13px]">
                    <span className="flex flex-wrap items-center gap-2"><span className="font-mono-ref font-medium text-jigsaw-navy">{o.booking_reference}</span><span className="text-jigsaw-muted">{u.unit_type}</span><span className="font-mono-ref text-jigsaw-muted">{o.check_in_date} → {o.check_out_date}</span><StatusBadge status={o.status} /></span>
                    <Button data-testid={`allocation-unallocate-${o.stay_id}`} size="sm" variant="ghost" className="h-7 px-2 text-jigsaw-danger" onClick={() => unallocate(o.stay_id)}><Unlink className="mr-1 h-3 w-3" /> Clear unit</Button>
                  </li>
                )))}
              </ul>
            </SectionCard>
          )}

          <SectionCard testId="allocations-unassigned-card" title="Unassigned stays at this property" subtitle="Active stays without a unit. Assignments are checked against capacity and overlapping stays.">
            {calendar.unassigned_stays.length === 0 ? (
              <p className="text-sm text-jigsaw-muted">Every active stay at this property has a unit assigned.</p>
            ) : (
              <ul className="divide-y divide-jigsaw-border">
                {calendar.unassigned_stays.map((s) => (
                  <li key={s.stay_id} data-testid={`unassigned-stay-${s.stay_id}`} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                    <span className="font-mono-ref text-jigsaw-navy">{s.booking_reference} &middot; {s.check_in_date || "—"} → {s.check_out_date || "—"} &middot; {s.room_count} room{s.room_count === 1 ? "" : "s"}</span>
                    <Button data-testid={`allocate-button-${s.stay_id}`} size="sm" variant="outline" onClick={() => setAllocateStay(s)}><Link2 className="mr-1 h-3.5 w-3.5" /> Allocate unit</Button>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </>
      )}

      <Dialog open={!!allocateStay} onOpenChange={(o) => !o && setAllocateStay(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Allocate a unit &mdash; {allocateStay?.booking_reference}</DialogTitle></DialogHeader>
          <Select value={unitChoice} onValueChange={setUnitChoice}>
            <SelectTrigger data-testid="allocation-unit-select"><SelectValue placeholder="Choose a unit" /></SelectTrigger>
            <SelectContent>{calendar?.units?.map((u) => <SelectItem key={u.id} value={u.id}>{u.unit_type} (cap. {u.capacity})</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter><Button data-testid="allocation-confirm-button" onClick={allocate} disabled={!unitChoice}>Allocate</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
